import { useState } from "react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  childrenOf,
  parentOf,
  pathLabelOf,
  pathOf,
  type Container,
} from "@egalregal/shared";
import { PATH_SEPARATOR } from "@egalregal/shared";
import { ArrowBackIcon, ChevronRightIcon } from "../../shared/components/icons";
import { ContainerMetaChips } from "../../shared/components/ContainerMetaChips";
import { useT } from "../../shared/i18n";

interface DestinationParentPickerProps {
  /** Every container in the chosen destination entry (flat). */
  containers: readonly Container[];
  /** Destination entry label, the first breadcrumb root. */
  entryLabel: string;
  /** Chosen parent; null means the entry root. */
  value: string | null;
  onChange: (parentId: string) => void;
  /**
   * Cycle-safe exclusions: the current container, every selected container and
   * their descendants. Excluded rows never appear in the tree or the search.
   */
  excludeIds?: ReadonlySet<string>;
  /**
   * Rows that may be drilled into but never chosen (bulk-move 06): the item
   * flow shows the source container so its children stay reachable, while the
   * source itself is excluded from the tree selection and the target search.
   */
  drillOnlyIds?: ReadonlySet<string>;
  /** Prefix for the search, breadcrumb, drill and row test ids. */
  testIdPrefix: string;
}

/**
 * Step two of the move flow: pick a parent inside one destination entry. The
 * tree drills one level at a time with a clickable breadcrumb and an up arrow;
 * the search field matches any container in the entry by name and shows its
 * full `entry / path` breadcrumb, so a deep target needs no drilling.
 *
 * Choosing a row only selects it; the dialog owns the confirm and root actions.
 */
export function DestinationParentPicker({
  containers,
  entryLabel,
  value,
  onChange,
  excludeIds,
  drillOnlyIds,
  testIdPrefix,
}: DestinationParentPickerProps) {
  const t = useT();
  const [drillId, setDrillId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const excluded = excludeIds ?? EMPTY_IDS;
  const drillOnly = drillOnlyIds ?? EMPTY_IDS;
  const available = containers.filter(
    (container) => !excluded.has(container.id) && !drillOnly.has(container.id),
  );
  const trail = drillId === null ? [] : pathOf(containers, drillId);
  const rows = childrenOf(containers, drillId).filter((container) => !excluded.has(container.id));
  const trimmed = query.trim();
  const normalized = trimmed.toLowerCase();
  const matches = normalized
    ? available.filter((container) => container.name.toLowerCase().includes(normalized))
    : [];

  /** Select a parent and leave the search; the tree follows to its level. */
  const choose = (parentId: string) => {
    onChange(parentId);
    setQuery("");
    setDrillId(parentOf(containers, parentId)?.id ?? null);
  };

  const drillInto = (containerId: string | null) => {
    setDrillId(containerId);
    setQuery("");
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        placeholder={t("bulkTargetSearch", { entry: entryLabel })}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mb: 1.5 }}
        inputProps={{ "data-testid": `${testIdPrefix}-search`, "aria-label": t("bulkTargetSearch", { entry: entryLabel }) }}
      />

      {trimmed.length > 0 ? (
        <>
          {matches.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-search-empty`}>
              {t("bulkTargetNoMatches", { query: trimmed })}
            </Typography>
          ) : (
            <List dense data-testid={`${testIdPrefix}-search-results`}>
              {matches.map((container) => (
                <ListItem key={container.id} disablePadding>
                  <ListItemButton
                    selected={value === container.id}
                    data-testid={`${testIdPrefix}-search-${container.name}`}
                    onClick={() => choose(container.id)}
                  >
                    <ListItemText
                      primary={container.name}
                      secondary={`${entryLabel}${PATH_SEPARATOR}${pathLabelOf(containers, container.id)}`}
                    />
                    <ContainerMetaChips
                      owner={container.owner}
                      kind={container.kind}
                      sx={{ ml: 1 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
            <IconButton
              size="small"
              aria-label={t("upOneLevel")}
              disabled={drillId === null}
              data-testid={`${testIdPrefix}-up`}
              onClick={() =>
                drillInto(drillId === null ? null : (parentOf(containers, drillId)?.id ?? null))
              }
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs aria-label={t("destinationPath")} data-testid={`${testIdPrefix}-breadcrumb`} sx={{ flexGrow: 1 }}>
              <Button
                size="small"
                disabled={drillId === null}
                data-testid={`${testIdPrefix}-crumb-root`}
                onClick={() => drillInto(null)}
                sx={{ minWidth: 0, px: 0.5, textTransform: "none" }}
              >
                {entryLabel}
              </Button>
              {trail.map((container, index) =>
                index === trail.length - 1 ? (
                  <Typography key={container.id} variant="body2" color="text.primary">
                    {container.name}
                  </Typography>
                ) : (
                  <Button
                    key={container.id}
                    size="small"
                    data-testid={`${testIdPrefix}-crumb-${container.name}`}
                    onClick={() => drillInto(container.id)}
                    sx={{ minWidth: 0, px: 0.5, textTransform: "none" }}
                  >
                    {container.name}
                  </Button>
                ),
              )}
            </Breadcrumbs>
          </Box>

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-empty`}>
              {t("moveNoContainers")}
            </Typography>
          ) : (
            <List dense data-testid={`${testIdPrefix}-list`}>
              {rows.map((container) => {
                const isDrillOnly = drillOnly.has(container.id);
                const nested = childrenOf(containers, container.id).some(
                  (child) => !excluded.has(child.id),
                );
                return (
                  <ListItem key={container.id} disablePadding>
                    <ListItemButton
                      selected={!isDrillOnly && value === container.id}
                      data-testid={`${testIdPrefix}-${container.name}`}
                      onClick={() => (isDrillOnly ? drillInto(container.id) : choose(container.id))}
                    >
                      <ListItemText primary={container.name} />
                      <ContainerMetaChips
                        owner={container.owner}
                        kind={container.kind}
                        sx={{ ml: 1 }}
                      />
                      {isDrillOnly && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t("itemBulkSourceNode")}
                          sx={{ ml: 1 }}
                        />
                      )}
                      {nested && (
                        <IconButton
                          size="small"
                          aria-label={t("drillInto", { name: container.name })}
                          data-testid={`${testIdPrefix}-drill-${container.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            drillInto(container.id);
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </>
      )}
    </>
  );
}

const EMPTY_IDS: ReadonlySet<string> = new Set();
